/**
 * useCapabilities Hook
 *
 * Fetches and manages organization capabilities based on operation mode
 * Used to dynamically show/hide UI elements based on standalone vs connected mode
 */

import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { apiService } from '../services/api';
import { useOrganizationStore } from '../stores/organizationStore';

export interface OrganizationCapabilities {
  patients: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  appointments: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  encounters: {
    enabled: boolean;
    canAssignUnmatched: boolean;
  };
  ui: {
    showPatients: boolean;
    showAppointments: boolean;
    showTemplates: boolean;
    showEncounters: boolean;
    dashboardMode: 'standard' | 'encounters-only';
  };
  /** Who may start a recording (AMP-136); missing from an older API, which let everyone record. */
  recording?: {
    canRecord: boolean;
  };
  mode: 'standalone' | 'connected';
  ehrStatus: {
    configured: boolean;
    systemId?: string;
    connected?: boolean;
  };
}

export const useCapabilities = () => {
  const { user } = useAuth();
  const [capabilities, setCapabilities] = useState<OrganizationCapabilities | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCapabilities = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const data = await apiService.get<any>('/v1/organizations/capabilities');
        setCapabilities(data);
        // Sync organization store so components using operationMode stay consistent
        useOrganizationStore.getState().setOperationMode(data.mode);
        useOrganizationStore.getState().setCanRecord(data.recording?.canRecord ?? true);
      } catch (err) {
        console.error('Error fetching capabilities:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');

        // Fallback to standalone mode if capabilities fetch fails
        setCapabilities({
          patients: { read: true, create: true, update: true, delete: true },
          appointments: { read: true, create: true, update: true, delete: true },
          encounters: { enabled: false, canAssignUnmatched: false },
          ui: {
            showPatients: true,
            showAppointments: true,
            // Standalone hides Templates (the server never shows them), so the fallback must not either.
            showTemplates: false,
            showEncounters: false,
            dashboardMode: 'standard'
          },
          mode: 'standalone',
          ehrStatus: { configured: false }
        });
        // Keep the persisted org store consistent with the standalone fallback. Without
        // this, a stale persisted 'connected' (e.g. from a prior connected-org session on
        // this browser) survives a failed capabilities fetch and silently disables
        // standalone patient/appointment creation, since create/edit gates read
        // operationMode from this store.
        useOrganizationStore.getState().setOperationMode('standalone');
        // Keep the last answer the server gave: a failed read must not turn a refusal into a grant.
        // With no answer yet, stay open; the recording routes enforce the rule themselves, so a
        // failed read does not lock anyone out.
        if (useOrganizationStore.getState().canRecord === null) {
          useOrganizationStore.getState().setCanRecord(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCapabilities();
  }, [user]);

  return {
    capabilities,
    loading,
    error,
    isStandaloneMode: capabilities?.mode === 'standalone',
    isConnectedMode: capabilities?.mode === 'connected',
    canCreatePatients: capabilities?.patients.create ?? false,
    canCreateAppointments: capabilities?.appointments.create ?? false,
    canViewEncounters: capabilities?.encounters.enabled ?? false,
    dashboardMode: capabilities?.ui.dashboardMode ?? 'standard'
  };
};
